import { ScrollArea, Button } from "@shortsworks/ui";
import {
  type GetStoriesParams,
  type StoryResData,
  useGetStories,
  useGetStoriesSuspense,
} from "@shortsworks/user-api";
import {LoaderCircle} from "lucide-react"
import { useEffect, useState } from "react";
import { cn } from "@shortsworks/ui/lib/utils";
import { PostingCard } from "@app/components/PostingList/PostingCard";
import { MapRenderer } from "@app/components/MapRenderer";

export interface PostingListProps {
  params?: GetStoriesParams;
  className?: string;
  disableMultiSelect?: boolean;
  disableSelect?: boolean;
  hideMoreMenu?: boolean;
  data?: any;
  gridCols?: number;
  onSelect?: (ids: string[]) => void;
  selectedIds?: string[];
  exceptIds?: string[];
}

export const PostingList = ({
  params = {},
  className,
  disableMultiSelect,
  disableSelect,
  hideMoreMenu,
  data,
  gridCols,
  onSelect,
  selectedIds,
}: PostingListProps) => {
  const {data: postingData, isSuccess: isSuccessGetStories, isPending, queryKey} = useGetStories(params);
  const [innerSelectedIds, setInnerSelectedIds] = useState<string[]>(
    selectedIds ?? [],
  );

  useEffect(() => {
    if (!selectedIds) return;
    setInnerSelectedIds(selectedIds ?? []);
  }, [selectedIds]);

  const handleSelect = (id: string, selected: boolean) => {
    if (disableSelect) return;

    if (disableMultiSelect) {
      if (innerSelectedIds.includes(id)) {
        setInnerSelectedIds([]);
        onSelect?.([]);
        return;
      }

      setInnerSelectedIds([id]);
      onSelect?.([id]);
      return;
    }

    // multi select
    let ids = [];
    if (selected) {
      ids = [...innerSelectedIds, id];
    } else {
      ids = innerSelectedIds.filter((prevId) => prevId !== id);
    }

    setInnerSelectedIds(ids);

    onSelect?.(ids);
  };

  if(!isSuccessGetStories) {
    return (
        <div className="flex flex-shrink items-center justify-center">
          <LoaderCircle className="w-6 h-6 mr-2 animate-spin" />
        </div>
    );
  }

  return (
    <div
      className={cn(
        "flex flex-col flex-1 w-full h-full overflow-hidden",
        className,
      )}
    >
      {/* 포스팅이 없는 경우 columns 나뉘는 현상 수정 필요 */}
      <ScrollArea type="scroll" className="w-full flex-1">
        <div
          className={cn(
            "grid grid-cols-3 md:grid-cols-4 lg:grid-cols-8 gap-2",
            gridCols &&
              `grid-cols-${gridCols} md:grid-cols-${gridCols} lg:grid-cols-${gridCols}`,
            !isPending &&
              !data &&
              postingData?.data &&
              postingData?.data.length < 1 &&
              "grid-cols-none md:grid-cols-none lg:grid-cols-none",
          )}
        >
          {/* loading 일 때 fallback으로 보이는 점 체크 필요*/}
          <MapRenderer
            data={data ?? postingData?.data}
            fallbackTitle="포스팅이 없습니다."
            render={(story: StoryResData) => {
              const groupIds = story.groups?.map((group) => group.id);
              return (
                <PostingCard
                  key={`posting-${story.id}`}
                  params={params}
                  storyId={story.id}
                  thumbnail={story.thumbnail}
                  title={story.name}
                  description={story.description}
                  groupIds={groupIds}
                  selectedIds={innerSelectedIds}
                  selected={innerSelectedIds.includes(story.id)}
                  onSelect={handleSelect}
                  hideMoreMenu={hideMoreMenu}
                  isPinned={story.isPinned}
                />
              );
            }}
          />
        </div>
      </ScrollArea>
    </div>
  );
};
